import IntlMessages from '@iso/components/utility/intlMessages';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/auth';
import { Link, useLocation } from 'react-router-dom';
import { matchPath, useNavigate } from 'react-router';
import Logo from '../../assets/images/new-logo.svg';
import SupportModal from '@/containers/Support/SupportModal';
import { Button, Divider, Select } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { CreateWorkspaceModal } from '../../components/Workspace/CreateWorkspaceModal';
import {
  BadgeHelp,
  LineChart,
  ChevronDown,
  ChevronUp,
  FileText,
  LayoutGrid,
  MessageSquareText,
  UserRound,
  Settings,
  PencilLine,
  Trash
} from "lucide-react";
import { EditWorkspaceModal } from '@/components/Workspace/EditWorkspaceModal';
import { DeleteWorkspaceModal } from '@/components/Workspace/DeleteWorkspaceModal';
import { HasPermission } from '@/helpers/permission';

export default function Sidebar({ open }) {
  const {
    Logout,
    user,
    partner,
    workspaces,
    ListWorkspaces,
    currentWorkspace,
    setCurrentWorkspace,
    signed,
  } = useAuth();

  const location = useLocation();
  const navigate = useNavigate();

  const startVisibility = !(localStorage.getItem('infoVisibility') === 'false');
  const startSettingsMenuOpen = !(
    localStorage.getItem('settingsMenuOpen') === 'false'
  );
  const startDocsMenuOpen = !(localStorage.getItem('docsMenuOpen') === 'false');

  const [infoVisibility, setInfoVisibility] = useState(startVisibility);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(
    startSettingsMenuOpen,
  );
  const [docsMenuOpen, setDocsMenuOpen] = useState(startDocsMenuOpen);
  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const [createWorkspaceModalOpen, setCreateWorkspaceModalOpen] =
    useState(false);
  const [editWorkspaceModalOpen, setEditWorkspaceModalOpen] = useState(false);
  const [deleteWorkspaceModalOpen, setDeleteWorkspaceModalOpen] =
    useState(false);
  const [openSelect, setOpenSelect] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState(currentWorkspace);

  useEffect(() => {
    if (signed) {
      ListWorkspaces();
    }
  }, []);

  useEffect(() => {
    setSelectedWorkspace(currentWorkspace);
  }, [currentWorkspace]);

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  const setVisibility = (item, value) => {
    if (item === 'infoVisibility') {
      setInfoVisibility(value);
    } else if (item === 'settingsMenuOpen') {
      setSettingsMenuOpen(value);
    } else if (item === 'docsMenuOpen') {
      setDocsMenuOpen(value);
    }
    localStorage.setItem(item, value);
  };

  const isActive = path => {
    const match = matchPath({ path }, location.pathname);

    if (match) {
      return 'bg-gray-100 text-primary font-bold';
    }
    return 'font-normal';
  };

  const isSubItemActive = path => {
    const match = matchPath({ path }, location.pathname);

    if (match) {
      return 'text-primary font-bold';
    }
    return 'font-normal';
  };

  const sidebarStyle = () => {
    if (open) {
      return '';
    }
    return '-translate-x-full';
  };

  function getWorkspaceById(workspaceId) {
    return workspaces.find(workspace => workspace.external_id === workspaceId);
  }

  return (
    <>
      <aside
        id="logo-sidebar"
        className={`fixed z-[50] overflow-y-auto w-64 h-[calc(var(--vh,1vh)*100)] transition-transform bg-white shadow-sm sm:translate-x-0 ${sidebarStyle()}`}
        aria-label="Sidebar"
      >
        <div className="min-h-screen overflow-auto">
          <div className="h-full  px-3 pb-4 bg-white">
            <div className="pb-5 pl-2 items-center justify-start flex">
              <a
                href="/"
                className="mt-2"
              >
                <img
                  src={Logo}
                  className="h-14 mr-3"
                  alt="Saffe"
                  width={100}
                />
              </a>
            </div>
            {signed && (
              <ul className="font-medium space-y-1 items-center p-2">
                <li className="flex justify-between pl-2 items-center group">
                  <span className="text-xl">{user?.name}</span>
                </li>
                <li>
                  <a
                    href="/my-account/personal"
                    className={`flex items-center p-2 text-gray-500 rounded-lg hover:bg-gray-100  group ${isActive(
                      '/my-account/*',
                    )}`}
                  >
                    <UserRound className="w-5 h-5" />
                    <span className="flex-1 ml-3 whitespace-nowrap">
                      <IntlMessages id="sidebar.queries.myAccount" />
                    </span>
                  </a>
                </li>
                {HasPermission('manage_financials') && (
                  <li>
                    <h4 className="flex items-center group px-2 text-gray-500 justify-between">
                      <div className="flex items-center">
                        <svg
                          className="flex-shrink-0 w-6 h-6 transition duration-75"
                          aria-hidden="true"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <path
                            d="M13.905 3.31598L17.633 7.99998M20 12H15C14.4696 12 13.9609 12.2107 13.5858 12.5858C13.2107 12.9608 13 13.4695 13 14V15C13 15.5304 13.2107 16.0391 13.5858 16.4142C13.9609 16.7893 14.4696 17 15 17H20M20 12C20.2652 12 20.5196 12.1053 20.7071 12.2929C20.8946 12.4804 21 12.7348 21 13V16C21 16.2652 20.8946 16.5196 20.7071 16.7071C20.5196 16.8946 20.2652 17 20 17M20 12V8.99998C20 8.73476 19.8946 8.48041 19.7071 8.29287C19.5196 8.10534 19.2652 7.99998 19 7.99998H4C3.73478 7.99998 3.48043 8.10534 3.29289 8.29287C3.10536 8.48041 3 8.73476 3 8.99998V20C3 20.2652 3.10536 20.5196 3.29289 20.7071C3.48043 20.8946 3.73478 21 4 21H19C19.2652 21 19.5196 20.8946 19.7071 20.7071C19.8946 20.5196 20 20.2652 20 20V17M13.633 7.99998L9.905 3.31598L4.352 7.99998H13.633Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <a
                          className="ml-3 font-bold"
                          href="/my-account/financial"
                        >
                          {infoVisibility
                            ? `${formatter.format(partner?.balance)}`
                            : '******'}
                        </a>
                      </div>
                      {infoVisibility ? (
                        <svg
                          onClick={() =>
                            setVisibility('infoVisibility', !infoVisibility)
                          }
                          className="cursor-pointer"
                          width="22"
                          height="16"
                          viewBox="0 0 22 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M19.8698 6.962C20.3438 7.582 20.3438 8.419 19.8698 9.038C18.3768 10.987 14.7948 15 10.6128 15C6.4308 15 2.8488 10.987 1.3558 9.038C1.12519 8.74113 1 8.37592 1 8C1 7.62408 1.12519 7.25887 1.3558 6.962C2.8488 5.013 6.4308 1 10.6128 1C14.7948 1 18.3768 5.013 19.8698 6.962V6.962Z"
                            stroke="#00ABAB"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M10.6133 11C12.2701 11 13.6133 9.65685 13.6133 8C13.6133 6.34315 12.2701 5 10.6133 5C8.95643 5 7.61328 6.34315 7.61328 8C7.61328 9.65685 8.95643 11 10.6133 11Z"
                            stroke="#00ABAB"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : (
                        <svg
                          onClick={() =>
                            setVisibility('infoVisibility', !infoVisibility)
                          }
                          className="cursor-pointer"
                          width="21"
                          height="9"
                          viewBox="0 0 21 9"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M1 1C1 1 4.325 5.30769 10.5 5.30769C16.675 5.30769 20 1 20 1M2.9 2.77154L1 5.30769M20 5.30769L18.1038 2.77477M7.5683 4.96308L6.7 8M13.4099 4.97169L14.3 8"
                            stroke="#00ABAB"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </h4>
                  </li>
                )}
              </ul>
            )}
            {signed && (
              <ul className="mt-6 space-y-1 font-medium">
                <span className="pl-2">Workspace</span>
                <Select
                  className="flex m-2"
                  size="large"
                  value={currentWorkspace}
                  open={openSelect}
                  onDropdownVisibleChange={() => setOpenSelect(!openSelect)}
                  showSearch
                  fieldNames={{ label: 'name', value: 'external_id' }}
                  filterOption={(input, option) =>
                    (option?.name ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={workspaces}
                  optionRender={option => (
                    <div className="flex justify-between items-center gap-1">
                      <span className="truncate flex-1">{option.label}</span>
                      <div className="flex items-center gap-2.5">
                        <PencilLine
                          onClick={e => {
                            e.stopPropagation();
                            setSelectedWorkspace(option.value);
                            setEditWorkspaceModalOpen(true);
                            setOpenSelect(false);
                          }}
                          className="w-4 h-4 text-gray-500"
                        />
                        {workspaces.length > 1 && (
                          <Trash
                            onClick={e => {
                              e.stopPropagation();
                              setSelectedWorkspace(option.value);
                              setDeleteWorkspaceModalOpen(true);
                              setOpenSelect(false);
                            }}
                            className="w-4 h-4 text-gray-500"
                          />
                        )}
                      </div>
                    </div>
                  )}
                  dropdownRender={menu => (
                    <>
                      {menu}
                      <Divider style={{ margin: '8px 0' }} />
                      <Button
                        className="w-full"
                        type="text"
                        icon={<PlusOutlined />}
                        onClick={e => {
                          e.stopPropagation();
                          setCreateWorkspaceModalOpen(true);
                          setOpenSelect(false);
                        }}
                      >
                        <IntlMessages id="sidebar.workspace.new_workspace.button" />
                      </Button>
                    </>
                  )}
                  onChange={value => {
                    setCurrentWorkspace(value);
                    navigate(0);
                  }}
                />
              </ul>
            )}
            {signed && (
              <ul className="mt-6 space-y-1 font-medium">
                <span className="pl-2">
                  <IntlMessages id="sidebar.menuLabel.general" />
                </span>
                <li>
                  <a
                    href="/"
                    className={`flex items-center p-2 text-gray-500 rounded-lg hover:bg-gray-100 group ${isActive(
                      '/',
                    )}`}
                  >
                    <LineChart className="w-5 h-5" />
                    <span
                      className={`flex-1 ml-3 whitespace-nowrap ${isActive('/')}`}
                    >
                      <IntlMessages id="sidebar.dashboard" />
                    </span>
                  </a>
                </li>
                {HasPermission('list_transactions') && (
                  <li>
                    <a
                      href="/transactions"
                      className={`flex items-center p-2 text-gray-500 rounded-lg  hover:bg-gray-100  group ${isActive(
                        '/transactions/*',
                      )}`}
                    >
                      <LayoutGrid className="w-5 h-5" />

                      <span className="flex-1 ml-3 whitespace-nowrap">
                        <IntlMessages id="sidebar.queries.transactions" />
                      </span>
                    </a>
                  </li>
                )}
              </ul>
            )}
            <ul className="mt-2 space-y-1 font-medium">
              <span className="pl-2">
                <IntlMessages id="sidebar.menuLabel.developer" />
              </span>
              {signed && (
                <li>
                  <button
                    type="button"
                    className={`flex items-center w-full p-2 text-base text-gray-500 transition duration-75 rounded-lg group hover:bg-gray-100 ${isActive(
                      '/settings/*',
                    )}`}
                    onClick={() =>
                      setVisibility('settingsMenuOpen', !settingsMenuOpen)
                    }
                  >
                    <Settings className="w-5 h-5" />
                    <span className="flex-1 ml-3 text-left font-normal whitespace-nowrap ">
                      <IntlMessages id="sidebar.queries.settings" />
                    </span>
                    {settingsMenuOpen && <ChevronDown className="w-5 h-5" />}
                    {!settingsMenuOpen && <ChevronUp className="w-5 h-5" />}
                  </button>
                  <ul
                    className={`multi-collapse ${
                      settingsMenuOpen ? 'visible' : 'hidden'
                    }`}
                  >
                    <li>
                      <a
                        href="/settings/capture"
                        className={`flex items-center w-full p-2 my-1 text-gray-500 transition duration-75 rounded-lg pl-11 group hover:bg-gray-100 ${isSubItemActive(
                          '/settings/capture',
                        )}`}
                      >
                        Capture
                      </a>
                    </li>
                    <li>
                      <a
                        href="/settings/integration"
                        className={`flex items-center w-full p-2 text-gray-500 transition duration-75 rounded-lg pl-11 group hover:bg-gray-100 ${isSubItemActive(
                          '/settings/integration',
                        )}`}
                      >
                        Integration
                      </a>
                    </li>
                  </ul>
                </li>
              )}
              <li>
                <button
                  type="button"
                  className={`flex items-center w-full p-2 text-base text-gray-500 transition duration-75 rounded-lg group hover:bg-gray-100 ${isActive(
                    '/docs/*',
                  )}`}
                  onClick={() => setVisibility('docsMenuOpen', !docsMenuOpen)}
                >
                  <FileText className="w-5 h-5" />
                  <span className="flex-1 ml-3 text-left font-normal whitespace-nowrap">
                    <IntlMessages id="sidebar.queries.docs" />
                  </span>
                  {docsMenuOpen && <ChevronDown className="w-5 h-5" />}
                  {!docsMenuOpen && <ChevronUp className="w-5 h-5" />}
                </button>
                <ul
                  className={`multi-collapse ${
                    docsMenuOpen ? 'visible' : 'hidden'
                  }`}
                >
                  <li>
                    <a
                      href="/docs/integration"
                      className={`flex items-center w-full p-2 text-gray-500 transition duration-75 rounded-lg pl-11 group hover:bg-gray-100 ${isSubItemActive(
                        '/docs/integration',
                      )}`}
                    >
                      Integration
                    </a>
                  </li>
                  <li>
                    <a
                      href="/docs/webhook"
                      className={`flex items-center w-full p-2 text-gray-500 transition duration-75 rounded-lg pl-11 group hover:bg-gray-100 ${isSubItemActive(
                        '/docs/webhook',
                      )}`}
                    >
                      Webhook
                    </a>
                  </li>
                  <li>
                    <a
                      target="_blank"
                      href="https://docs-api.go.saffe.ai"
                      className="flex items-center w-full font-normal p-2 text-gray-500 transition duration-75 rounded-lg pl-11 group hover:bg-gray-100"
                      rel="noreferrer"
                    >
                      REST API
                    </a>
                  </li>
                </ul>
              </li>
            </ul>
            <ul className="mt-2 space-y-1 font-medium">
              <span className="pl-2">
                <IntlMessages id="sidebar.menuLabel.help" />
              </span>
              <li>
                <Link
                  to="/faq"
                  className={`flex items-center p-2 text-gray-500 rounded-lg hover:bg-gray-100 group ${isActive(
                    '/faq',
                  )}`}
                >
                  <BadgeHelp className="w-6 h-6" />

                  <span className="flex-1 ml-3 whitespace-nowrap">FAQ</span>
                </Link>
              </li>
              {signed && (
                <li>
                  <Link
                    className={`flex items-center p-2 text-gray-500 rounded-lg hover:bg-gray-100 group ${isActive(
                      '/support',
                    )}`}
                    onClick={() => setSupportModalOpen(true)}
                  >
                    <MessageSquareText className="w-6 h-6" />

                    <span className="flex-1 ml-3 whitespace-nowrap">
                      <IntlMessages id="sidebar.queries.support" />
                    </span>
                  </Link>
                </li>
              )}
            </ul>
            {signed && (
              <ul>
                <li className="sm:hidden">
                  <Link
                    onClick={() => Logout()}
                    className="flex items-center p-2 text-gray-500 rounded-lg  hover:bg-gray-100  group"
                  >
                    <svg
                      className="w-6 h-6 text-gray-500 transition duration-75 group-hover:text-gray-900 "
                      viewBox="0 0 18 15"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 7.5H12M12 7.5L8 3.78571M12 7.5L8 11.2143M12 1H15C15.5304 1 16.0391 1.19566 16.4142 1.54394C16.7893 1.89223 17 2.3646 17 2.85714V12.1429C17 12.6354 16.7893 13.1078 16.4142 13.4561C16.0391 13.8043 15.5304 14 15 14H12"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>

                    <span className="flex-1 ml-3 whitespace-nowrap">
                      <IntlMessages id="topbar.logout" />
                    </span>
                  </Link>
                </li>
              </ul>
            )}
          </div>
        </div>
      </aside>
      <CreateWorkspaceModal
        onSubmit={() => setCreateWorkspaceModalOpen(false)}
        open={createWorkspaceModalOpen}
        onCancel={() => setCreateWorkspaceModalOpen(false)}
      />
      { editWorkspaceModalOpen && (
        <EditWorkspaceModal
          onSubmit={() => setEditWorkspaceModalOpen(false)}
          open={editWorkspaceModalOpen}
          onCancel={() => setEditWorkspaceModalOpen(false)}
          workspace={getWorkspaceById(selectedWorkspace)}
        />
      )}
      { deleteWorkspaceModalOpen && (
        <DeleteWorkspaceModal
        onSubmit={() => setDeleteWorkspaceModalOpen(false)}
        open={deleteWorkspaceModalOpen}
        onCancel={() => setDeleteWorkspaceModalOpen(false)}
        workspace={getWorkspaceById(selectedWorkspace)}
      />
      )}
      { supportModalOpen && (
        <SupportModal
          onSubmit={() => setSupportModalOpen(false)}
          open={supportModalOpen}
          onCancel={() => setSupportModalOpen(false)}
        />
      )}
    </>
  );
}
